import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { InsurancePolicy } from '@/types/insurance';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

const premiumPaymentSchema = z.object({
  amount: z.number().positive('Amount must be positive'),
  payment_date: z.string().refine(val => !isNaN(Date.parse(val)), 'Invalid date'),
  notes: z.string().optional(),
});

export type PremiumPaymentFormData = z.infer<typeof premiumPaymentSchema>;

interface PremiumPaymentFormProps {
  policy: InsurancePolicy;
  onSubmit: (data: PremiumPaymentFormData) => void;
  isLoading?: boolean;
}

export function PremiumPaymentForm({
  policy,
  onSubmit,
  isLoading = false,
}: PremiumPaymentFormProps) {
  const form = useForm<PremiumPaymentFormData>({
    resolver: zodResolver(premiumPaymentSchema),
    defaultValues: {
      amount: policy.premium_amount,
      payment_date: new Date().toISOString().split('T')[0],
      notes: '',
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2">
          <div className="text-sm text-muted-foreground">
            <p>Policy: {policy.policy_type} - {policy.provider_name}</p>
            <p>Policy Number: {policy.policy_number}</p>
          </div>
        </div>

        <FormField
          control={form.control}
          name="amount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Amount ({policy.currency})</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  {...field}
                  onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="payment_date"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Payment Date</FormLabel>
              <FormControl>
                <Input type="date" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes (optional)</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Add any notes about this payment..."
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-4">
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Recording...' : 'Record Payment'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
